import React, { FC, useState } from 'react'
import { Column } from 'primereact/column'
import { PrimeIcons } from 'primereact/api'

import {
    NfcTag,
    Pool,
    useCompanyGetPoolsWithNfcInfoForTagQuery,
    useCompanySetPoolsNfcInfoForTagMutation,
} from '../../../../redux/api/web-api'
import { Table } from '../../../../uikit/Table'
import useText from '../../../../hooks/useText'
import { Input } from '../../../../uikit/Input'
import { Switch } from '../../../../uikit/Switch'
import { Dialog } from '../../../../uikit/Dialog'

type Props = {
    visible: boolean
    onHide: () => void
    rfidTag?: NfcTag
}

export const RfidTagPoolsDialog: FC<Props> = ({ visible, onHide, rfidTag }) => {
    const text = useText()
    const { data: pools, isLoading: poolsIsLoading } = useCompanyGetPoolsWithNfcInfoForTagQuery(
        {
            companyId: rfidTag?.companyId || '',
            nfcTagId: rfidTag?.id || '',
        },
        { skip: !rfidTag?.companyId || !rfidTag?.id, refetchOnMountOrArgChange: true }
    )

    const [loadingStates, setLoadingStates] = useState<{ [connectorId: string]: boolean }>({})

    const [updatePool] = useCompanySetPoolsNfcInfoForTagMutation()
    const setNfcAccess = async (hasNfcAccess: boolean, pool: Pool) => {
        setLoadingStates((prev) => ({ ...prev, [`${pool.id}`]: true }))

        try {
            if (rfidTag) {
                await updatePool({
                    body: [{ companyId: rfidTag.companyId!, id: pool.id, hasNfcAccess }],
                    companyId: rfidTag.companyId!,
                    nfcTagId: rfidTag.id!,
                }).unwrap()
            }
        } catch (err) {
            // ignore
        } finally {
            setLoadingStates((prev) => ({ ...prev, [`${pool.id}`]: false }))
        }
    }

    const [globalFilter, setGlobalFilter] = useState('')

    const resetDialogState = () => {
        setGlobalFilter('')
        setLoadingStates({})
    }

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            onShow={resetDialogState}
            header={text('lblRFIDAccessToPools')}
            style={{ width: '50rem' }}
        >
            <div className="flex flex-wrap align-items-center justify-content-end mb-3 pt-1">
                <Input
                    id="pools-search-input"
                    value={globalFilter}
                    type="search"
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder={text('lblSearch')}
                    icon={PrimeIcons.SEARCH}
                />
            </div>

            <Table
                value={pools}
                loading={poolsIsLoading}
                paginator
                scrollable
                scrollHeight="30rem"
                globalFilter={globalFilter}
                emptyMessage={text('msgNoPoolsFound')}
                tableClassName="min-w-full"
            >
                <Column field="name" header={text('lblName')} />
                <Column
                    body={(pool: Pool) => (
                        <Switch
                            checked={!!pool.hasNfcAccess}
                            onChange={(event) => {
                                void setNfcAccess(event.value, pool)
                            }}
                            loading={loadingStates[`${pool.id}`]}
                            inputId={`switch-nfc-access-${pool.id}`}
                            pt={{
                                input: {
                                    'aria-label': text('lblPoolRfidAccessSwitch', { name: pool.name }),
                                    'data-testid': `switch-nfc-access-${pool.id}`,
                                },
                            }}
                        />
                    )}
                    header={text('lblRFIDAccess')}
                />
            </Table>
        </Dialog>
    )
}
